import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getSlides, getSlidesQueryKey } from 'api/slides';
import { Slide } from 'interfaces/slide';
import { compact, filter, flattenDeep, isEmpty, map, uniq, uniqBy } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { getBatches } from 'utils/batching';
import { filtersQueryParamsSchema } from 'utils/useEncodedFilters/constants';

import { getPendingSlidesBaseParams, getPendingSlidesParamsString } from '../ProceduresPage/usePendingSlides';
import { ManifestDialogUrlState } from './dialogState';
import { addHydratedManifestSlides } from './manifestDraft';

type UpdateManifestDialogUrlState = (
  updates: Partial<ManifestDialogUrlState> | ((previousState: ManifestDialogUrlState) => ManifestDialogUrlState)
) => void;

const getEncodedSlideIds = ({ labId, slideIdsToInclude }: { labId: string; slideIdsToInclude: string[] }) =>
  getPendingSlidesParamsString({
    queryParamsSchema: filtersQueryParamsSchema,
    params: {
      ...getPendingSlidesBaseParams({ filters: undefined, labId, slideIdsToInclude }),
      skipPagination: true,
    },
  });

const slidePendingStates = [true, false];

const getMissingSlideCount = (requestedSlideIds: string[], fetchedSlides: Slide[]) => {
  const fetchedSlideIds = new Set(map(fetchedSlides, (slide) => String(slide.id)));

  return filter(uniq(requestedSlideIds), (slideId) => !fetchedSlideIds.has(slideId)).length;
};

const useFetchManifestSlidesByIds = (labId: string) => {
  const queryClient = useQueryClient();

  return useCallback(
    async (slideIdsToLoad: string[]) => {
      const fetchedSlidesByBatch = await Promise.all(
        map(getBatches(slideIdsToLoad, 100), async (batch) => {
          const encodedParams = getEncodedSlideIds({ labId, slideIdsToInclude: batch });
          return Promise.all(
            map(slidePendingStates, (pending) => {
              const ignoreLabId = !pending;
              const queryKey = getSlidesQueryKey({
                queryKeyInput: encodedParams,
                pending,
                ignoreLabId,
              });
              const cachedSlides = queryClient.getQueryData<Slide[]>(queryKey);

              if (cachedSlides) {
                return cachedSlides;
              }

              return queryClient.fetchQuery(queryKey, () =>
                getSlides({ queryParams: encodedParams, pending, ignoreLabId })
              );
            })
          );
        })
      );

      return uniqBy(compact(flattenDeep(fetchedSlidesByBatch)), 'id');
    },
    [labId, queryClient]
  );
};

export const useManifestSlideMetadata = ({
  labId,
  manualManifestGroupIntoSingleCase,
  manualManifestSlideIdsToLoad,
  open,
  updateManifestDialogUrlState,
}: {
  labId: string;
  manualManifestGroupIntoSingleCase: boolean;
  manualManifestSlideIdsToLoad: string[];
  open: boolean;
  updateManifestDialogUrlState: UpdateManifestDialogUrlState;
}) => {
  const fetchSlidesByIdBatches = useFetchManifestSlidesByIds(labId);
  const [isHydratingManualManifestSlides, setIsHydratingManualManifestSlides] = useState(false);

  useEffect(() => {
    if (!open || isEmpty(manualManifestSlideIdsToLoad)) {
      return;
    }

    let isCancelled = false;

    const hydrateManualManifestCasesFromSlideIds = async () => {
      setIsHydratingManualManifestSlides(true);

      try {
        const fetchedSlides = await fetchSlidesByIdBatches(manualManifestSlideIdsToLoad);

        if (isCancelled) {
          return;
        }

        const missingSlideCount = getMissingSlideCount(manualManifestSlideIdsToLoad, fetchedSlides);

        updateManifestDialogUrlState((previousState) => ({
          ...addHydratedManifestSlides({
            previousState,
            slides: fetchedSlides,
            labId,
            groupIntoSingleCase: manualManifestGroupIntoSingleCase,
          }),
          manualManifestSlideIdsToLoad: [],
          manualManifestGroupIntoSingleCase: false,
          manualManifestErrorMessages:
            missingSlideCount > 0
              ? [
                  `Could not load metadata for ${missingSlideCount} selected slide${
                    missingSlideCount === 1 ? '' : 's'
                  }.`,
                ]
              : [],
        }));
      } catch (error) {
        if (isCancelled) {
          return;
        }

        console.error('Error occurred while loading slide metadata for case assignment:', error);
        updateManifestDialogUrlState((previousState) => ({
          ...previousState,
          manualManifestSlideIdsToLoad: [],
          manualManifestGroupIntoSingleCase: false,
          manualManifestErrorMessages: ['Could not load slide metadata for case assignment. Please try again.'],
        }));
      } finally {
        if (!isCancelled) {
          setIsHydratingManualManifestSlides(false);
        }
      }
    };

    void hydrateManualManifestCasesFromSlideIds();

    return () => {
      isCancelled = true;
    };
  }, [
    fetchSlidesByIdBatches,
    labId,
    manualManifestGroupIntoSingleCase,
    manualManifestSlideIdsToLoad,
    open,
    updateManifestDialogUrlState,
  ]);

  return { fetchSlidesByIdBatches, isHydratingManualManifestSlides };
};

export const useAccessibleManifestSlides = ({
  enabled,
  labId,
  slideIds,
}: {
  enabled: boolean;
  labId: string;
  slideIds: string[];
}) => {
  const fetchSlidesByIdBatches = useFetchManifestSlidesByIds(labId);
  const uniqueSlideIds = uniq(slideIds);
  const shouldHydrateSlides = enabled && !isEmpty(uniqueSlideIds);
  const query = useQuery({
    queryKey: ['accessibleManifestSlides', labId, uniqueSlideIds],
    queryFn: () => fetchSlidesByIdBatches(uniqueSlideIds),
    enabled: shouldHydrateSlides,
  });

  return {
    slides: query.data || [],
    inaccessibleSlideCount: query.data ? getMissingSlideCount(uniqueSlideIds, query.data) : 0,
    isLoading: shouldHydrateSlides && query.isLoading,
    isError: query.isError,
  };
};
